import { ChevronDown } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { TemplateConfig } from "@/types/email-templates";

interface ButtonSettingsProps {
  config: TemplateConfig;
  isOpen: boolean;
  onToggle: () => void;
  onButtonChange: (key: keyof TemplateConfig["buttons"], value: string | number | boolean) => void;
  isEnabled?: boolean;
}

const ButtonSettings = ({ config, isOpen, onToggle, onButtonChange, isEnabled = true }: ButtonSettingsProps) => {
  const buttonStyles = [
    { value: "rounded", label: "Rounded (8px)" },
    { value: "square", label: "Square (0px)" },
    { value: "pill", label: "Pill (999px)" },
  ];

  return (
    <Card className={!isEnabled ? "opacity-50" : ""}>
      <Collapsible open={isOpen && isEnabled} onOpenChange={isEnabled ? onToggle : undefined}>
        <CollapsibleTrigger asChild>
          <CardHeader className={isEnabled ? "cursor-pointer hover:bg-muted/50 transition-colors" : "cursor-not-allowed"}>
            <CardTitle className="text-lg flex items-center gap-2">
              Button & Layout
              <ChevronDown
                className="ml-auto h-4 w-4 transition-transform"
                style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}
              />
            </CardTitle>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="space-y-4">
            {!isEnabled && (
              <div className="p-3 bg-muted rounded-md border border-muted-foreground/20">
                <p className="text-sm text-muted-foreground">
                  Button styles apply when the agent has a redirect URL configured
                </p>
              </div>
            )}
            {/* Border Radius */}
            <div>
              <Label htmlFor="borderRadius" className="text-sm">
                Border Radius (px)
              </Label>
              <div className="flex gap-2 mt-1">
                <Input
                  id="borderRadius"
                  type="number"
                  min="0"
                  max="24"
                  value={config.buttons?.borderRadius ?? 8}
                  onChange={(e) => onButtonChange("borderRadius", parseInt(e.target.value) || 0)}
                  disabled={!isEnabled}
                  className="text-sm flex-1"
                />
                <div className="flex items-center px-3 bg-muted rounded text-sm font-medium">
                  {config.buttons?.borderRadius ?? 8}px
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Applied to cards, sections, and containers
              </p>
            </div>

            {/* Button Style */}
            <div>
              <Label htmlFor="buttonStyle" className="text-sm">
                Button Style
              </Label>
              <Select
                value={config.buttons?.buttonStyle || "rounded"}
                onValueChange={(value) => isEnabled && onButtonChange("buttonStyle", value)}
                disabled={!isEnabled}
              >
                <SelectTrigger id="buttonStyle" className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {buttonStyles.map((style) => (
                    <SelectItem key={style.value} value={style.value}>
                      {style.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Affects styling of metric boxes and visual elements
              </p>
            </div>

            {/* Shadow Enabled */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="shadowEnabled" className="text-sm">
                  Enable Shadow
                </Label>
                <p className="text-xs text-muted-foreground">
                  Add box shadow to main container
                </p>
              </div>
              <Switch
                id="shadowEnabled"
                checked={config.buttons?.shadowEnabled ?? true}
                onCheckedChange={(checked) => isEnabled && onButtonChange("shadowEnabled", checked)}
                disabled={!isEnabled}
              />
            </div>

            {/* Button Text */}
            <div>
              <Label htmlFor="buttonText" className="text-sm">
                Button Text
              </Label>
              <Input
                id="buttonText"
                type="text"
                value={config.buttons?.buttonText || "View Full Report"}
                onChange={(e) => onButtonChange("buttonText", e.target.value)}
                disabled={!isEnabled}
                className="text-sm mt-1"
                placeholder="View Full Report"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Text displayed on the CTA button
              </p>
            </div>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};

export default ButtonSettings;
